/**
 * /mobile-broadband — SEO landing page
 * Target: "mobile broadband", "mobile broadband UK", "4G broadband", "5G broadband",
 *         "broadband without landline", "home broadband no phone line"
 * Research: Three + Vodafone rank here but as hardware retailers.
 * Silux angle: SIM-only mobile broadband — cheaper, no engineer, no landline.
 */
import { Link } from 'react-router-dom'
import { CheckCircle, ArrowRight, Wifi, Zap, MapPin, Shield, Signal } from 'lucide-react'
import SEOHead from '@/components/SEOHead'
import { rollingFaqs } from '@/data/rollingPlans'
import SiluxPromise from '@/components/SiluxPromise'

const FAQS = [
  ...rollingFaqs().slice(0, 3),
  {
    question: 'What is mobile broadband?',
    answer: 'Mobile broadband is internet access delivered via a 4G or 5G mobile network rather than a fixed phone line. You use a SIM card in a MiFi router, dongle or compatible router to connect multiple devices to the internet wirelessly.',
  },
  {
    question: 'How fast is mobile broadband UK?',
    answer: 'With Silux Mobile on Three\'s 5G network, typical download speeds on 4G are 10–50 Mbps with Broadband Lite and 50–150 Mbps with 5G Premium, and 80–400 Mbps on 5G. This is fast enough for 4K streaming, video calls, gaming and working from home.',
  },
  {
    question: 'Is mobile broadband better than fibre?',
    answer: 'Mobile broadband is far faster to set up — no engineer, no phone line, no socket drilled into the wall — and it is portable, so it moves with you if you move home within the UK. You pick the plan length yourself, from a 1-month rolling contract up to 36 months, and it is often cheaper than fibre. It is ideal for renters, rural areas, temporary accommodation and people who move frequently. For very heavy data use, fibre may still be preferable.',
  },
  {
    question: 'Can I get unlimited mobile broadband?',
    answer: 'Yes — Silux Mobile offers unlimited data mobile broadband plans on Three\'s 5G network. Unlimited data with no speed throttling (fair use policy applies).',
  },
  {
    question: 'What equipment do I need for mobile broadband?',
    answer: 'Nothing you have to buy separately — a 4G or 5G router (or a portable MiFi) is included in your package and delivered to your door ready to use. If you already own an unlocked router with a SIM slot, you can use that instead; we can advise on compatible hardware.',
  },
  {
    question: 'How long is a mobile broadband plan?',
    answer: 'You choose the term, from 1 month: a rolling monthly contract from £25.95/month (1-month minimum term, 30 days’ notice to leave, £30 refundable holding deposit), or a fixed 3, 6, 12, 18, 24 or 36 months. The longer plans carry the lowest monthly price — Broadband Lite and the MiFi Portable are £15.95/month over 24 months, and 5G Premium starts at £19.95/month over 36 months. 3-month plans start at £19.95/month plus a £15 refundable holding deposit.',
  },
  {
    question: 'Does mobile broadband work in rural areas?',
    answer: 'Yes — Three\'s network covers 99% of the UK population outdoors. Mobile broadband is often the only viable high-speed internet option for properties in rural areas where fibre or cable isn\'t available.',
  },
  {
    question: 'Will my broadband price increase during my contract?',
    answer: 'No. Your price is fixed for the life of your plan — there are no price increases during your contract and no annual inflation-linked price rises. What you sign up at is what you pay.',
  },
  {
    question: 'Are broadband upgrades free?',
    answer: 'Yes. Silux Mobile offers free broadband upgrades — move to a faster plan or upgrade your router with no upgrade fee. Free upgrades are included as standard.',
  },
  {
    question: 'Is Silux Mobile a good mobile broadband provider?',
    answer: 'Yes. Silux Mobile is a UK mobile broadband and fast internet provider on the Three, O2 and Vodafone networks — home broadband without a landline, from £15.95/month with the router included, plus no price increases during your contract and free broadband upgrades.',
  },
]

const SPEED_COMPARISONS = [
  { activity: 'Web browsing', required: '1-5 Mbps', mobile: '✅ Easily handled' },
  { activity: 'Video calls (Zoom, Teams)', required: '5-15 Mbps', mobile: '✅ No problem' },
  { activity: 'HD streaming (Netflix)', required: '5-25 Mbps', mobile: '✅ Smooth playback' },
  { activity: '4K streaming', required: '25-50 Mbps', mobile: '✅ With 5G' },
  { activity: 'Online gaming', required: '10-25 Mbps', mobile: '✅ With 5G' },
  { activity: 'Smart home devices', required: '1-5 Mbps each', mobile: '✅ Multi-device ready' },
]

export default function MobileBroadband() {
  const breadcrumbs = [
    { name: 'Home', url: 'https://www.siluxmobile.co.uk/' },
    { name: 'Mobile Broadband', url: 'https://www.siluxmobile.co.uk/mobile-broadband' },
  ]

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQS.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: { '@type': 'Answer', text: faq.answer },
    })),
  }

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Mobile Broadband UK',
    description: '4G and 5G mobile broadband for home and business use on the Three, O2 and Vodafone networks. Router included, no landline and no engineer needed. Plans from 1 month (rolling) to 36 months, from £15.95 a month.',
    provider: { '@type': 'Organization', name: 'Silux Mobile', url: 'https://www.siluxmobile.co.uk' },
    serviceType: 'Mobile Broadband',
    areaServed: 'United Kingdom',
  }

  return (
    <>
      <SEOHead
        title="Mobile Broadband UK | 4G & 5G Home Broadband | Silux Mobile"
        description="Get mobile broadband from Silux Mobile — 4G & 5G home internet without a landline. From £15.95/month with the router included. No engineer, no phone line. Plans from 1 month rolling to 36 months on Three, O2 and Vodafone. No price rises during your contract and free broadband upgrades."
        keywords={[
          'mobile broadband', 'mobile broadband UK', '4G broadband', '5G broadband UK',
          'broadband without landline', 'short term broadband', 'home broadband no phone line',
          'mobile broadband for home', 'rural broadband UK', '5G home broadband',
          'SIM only broadband', 'mobile internet UK', '4G home broadband',
          'mobile broadband provider', 'best mobile broadband provider UK', 'fast internet provider',
          'fast mobile broadband', '4G broadband provider', '5G broadband provider',
          'home broadband provider', 'broadband provider without landline', 'wireless broadband provider',
          'internet provider UK',
        ]}
        canonical="https://www.siluxmobile.co.uk/mobile-broadband"
        breadcrumbs={breadcrumbs}
        faqs={FAQS}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(serviceSchema) }} />

      {/* Hero */}
      <section className="relative py-20 px-4 bg-gradient-to-br from-purple-700 via-purple-600 to-blue-600 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('/grid.svg')]" />
        <div className="max-w-5xl mx-auto relative text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 rounded-full px-4 py-2 mb-6 text-sm font-medium">
            <Wifi className="w-4 h-4" />
            4G & 5G Mobile Broadband — No Landline Needed
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Mobile Broadband UK
            <span className="block text-2xl md:text-3xl font-normal text-white/80 mt-2">
              Fast home internet without a phone line — from £15.95/month
            </span>
          </h1>
          <p className="text-lg text-white/80 max-w-2xl mx-auto mb-8">
            Silux Mobile is a UK mobile broadband provider and fast internet provider, using the Three, O2 and Vodafone 4G and 5G networks to deliver fast home internet
            without a phone line or an engineer visit. Your router is included and delivered to your door, you pick a plan length from 1 month rolling to 36 months, and it comes with you when you move — ideal for renters,
            rural areas, and anyone wanting broadband on their own terms.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/broadband/guide"
              className="px-8 py-4 bg-white text-purple-700 rounded-2xl font-bold shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
            >
              Find My Package
            </Link>
            <Link
              to="/broadband"
              className="px-8 py-4 bg-white/20 border-2 border-white/40 text-white rounded-2xl font-bold hover:bg-white/30 transition-all"
            >
              View All Plans
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-6 mt-10 text-sm text-white/70">
            <span className="flex items-center gap-2"><Zap className="w-4 h-4 text-yellow-400" /> 80–400 Mbps on 5G</span>
            <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-green-400" /> Plans From 1 Month</span>
            <span className="flex items-center gap-2"><Signal className="w-4 h-4 text-blue-300" /> 99% UK Coverage</span>
          </div>
        </div>
      </section>

      <SiluxPromise variant="broadband" />

      {/* What is Mobile Broadband — definitional paragraph for AI Overviews */}
      <section className="py-12 px-4 bg-white border-b border-gray-100">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">What is Mobile Broadband?</h2>
          <p className="text-gray-600 text-lg leading-relaxed text-center">
            <strong>Mobile broadband</strong> is high-speed internet delivered over a mobile (4G or 5G) network.
            Instead of a fixed phone line, you connect via a SIM card inserted into a MiFi router, 4G router, USB dongle or portable hotspot.
            Silux Mobile provides mobile broadband on the <strong>Three, O2 and Vodafone networks</strong> — with no landline required,
            no engineer visits, the router included in the package and plans from just £15.95/month.
          </p>
        </div>
      </section>

      {/* 4G vs 5G */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">4G vs 5G Mobile Broadband</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-white rounded-2xl p-8 border-2 border-blue-200">
              <h3 className="text-xl font-bold text-blue-700 mb-4">4G Broadband</h3>
              <ul className="space-y-3 text-sm">
                {[
                  'Typical speeds: 10–50 Mbps (Lite) or 50–150 Mbps (5G Premium on 4G)',
                  'Excellent UK-wide coverage',
                  'Ideal for streaming, browsing, WFH',
                  'Available in rural areas',
                  'Great value from £15.95/month',
                ].map(f => (
                  <li key={f} className="flex items-center gap-2 text-gray-700">
                    <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link to="/broadband/mifi" className="block mt-6 text-center py-3 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-700 transition-all">
                View 4G Plans
              </Link>
            </div>
            <div className="bg-white rounded-2xl p-8 border-2 border-purple-400">
              <div className="flex items-center gap-2 mb-4">
                <h3 className="text-xl font-bold text-purple-700">5G Broadband</h3>
                <span className="text-xs font-bold bg-purple-100 text-purple-700 px-2 py-1 rounded-full">Recommended</span>
              </div>
              <ul className="space-y-3 text-sm">
                {[
                  'Typical speeds: 80–400 Mbps download',
                  'Ultra-low latency for gaming',
                  'Ideal for 4K streaming, gaming, large downloads',
                  'Expanding UK coverage rapidly',
                  'Unlimited data on every plan',
                ].map(f => (
                  <li key={f} className="flex items-center gap-2 text-gray-700">
                    <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <Link to="/broadband/premium" className="block mt-6 text-center py-3 bg-purple-600 text-white rounded-xl font-semibold hover:bg-purple-700 transition-all">
                View 5G Plans
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Speed vs Activity */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">
            Is Mobile Broadband Fast Enough?
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="bg-purple-600 text-white">
                  <th className="px-4 py-3 text-left rounded-tl-xl">Online Activity</th>
                  <th className="px-4 py-3 text-center">Speed Required</th>
                  <th className="px-4 py-3 text-center rounded-tr-xl">Mobile Broadband</th>
                </tr>
              </thead>
              <tbody>
                {SPEED_COMPARISONS.map((row, i) => (
                  <tr key={row.activity} className={`border-b border-gray-100 ${i % 2 === 0 ? 'bg-gray-50' : 'bg-white'}`}>
                    <td className="px-4 py-3 font-medium text-gray-800">{row.activity}</td>
                    <td className="px-4 py-3 text-center text-gray-600">{row.required}</td>
                    <td className="px-4 py-3 text-center text-green-600 font-semibold">{row.mobile}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* GEO */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-4">
            Mobile Broadband Across the UK
          </h2>
          <p className="text-center text-gray-600 mb-8">
            Whether you're in a city centre or a rural village, Three's network has you covered.
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              'London', 'Manchester', 'Birmingham', 'Leeds',
              'Glasgow', 'Edinburgh', 'Bristol', 'Cardiff',
              'Rural England', 'Rural Scotland', 'Rural Wales', 'Rural N. Ireland',
              'Caravans & Motorhomes', 'Holiday Homes', 'Temporary Sites', 'Remote Offices',
            ].map(area => (
              <div key={area} className="bg-white rounded-xl px-4 py-3 text-center text-sm font-medium text-gray-700 border border-gray-200">
                {area}
              </div>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link
              to="/network-coverage"
              className="inline-flex items-center gap-2 text-purple-600 font-semibold hover:underline"
            >
              <MapPin className="w-4 h-4" />
              Check your postcode coverage
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">Mobile Broadband FAQs</h2>
          <div className="space-y-4">
            {FAQS.map(faq => (
              <details key={faq.question} className="group border border-gray-200 rounded-xl overflow-hidden">
                <summary className="flex items-center justify-between p-5 cursor-pointer font-semibold text-gray-900 hover:bg-gray-50">
                  {faq.question}
                  <ArrowRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <div className="px-5 pb-5 text-gray-600 text-sm leading-relaxed">{faq.answer}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="py-10 px-4 bg-purple-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-bold text-gray-900 mb-4 text-center">Related Services</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: 'Rolling Monthly Broadband', to: '/rolling-monthly-broadband' },
              { label: 'Cheap Broadband', to: '/cheap-broadband' },
              { label: 'Data SIM Cards', to: '/data-sim-cards' },
              { label: 'Cheap SIM Cards', to: '/cheap-sim-cards' },
              { label: 'Starlink Broadband', to: '/broadband/starlink' },
              { label: 'Broadband Guide', to: '/broadband/guide' },
              { label: 'SIM Only Deals', to: '/sim-only' },
              { label: 'Network Coverage', to: '/network-coverage' },
              { label: 'Contact Us', to: '/contact' },
            ].map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="block p-3 bg-white rounded-xl border border-gray-200 text-sm font-medium text-gray-700 hover:border-purple-300 hover:text-purple-700 transition-all text-center"
              >
                {link.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  )
}
